import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentData,
  type Firestore,
  type Query,
} from 'firebase/firestore'
import type { BreakdownItem } from '@domain/finance'
import type { BreakdownRepository } from '@domain/ports/finance-repository'
import {
  optionalText,
  requireCents,
  requireIso,
  requireOneOf,
  requireText,
  textList,
} from './converters'
import { HOUSEHOLD_ID } from './household-repository'

/**
 * Os itens de detalhamento de uma transação.
 *
 * ## Coleção própria, e não um array na transação
 *
 * `Docs/02-Dominio/Breakdown.md` decidiu isso: *"uma nota de mercado gera dezenas
 * de itens, array grande tem escrita cara e concorrente, e `receiptItemIds` fica
 * melhor consultável assim"*. A consequência prática aqui é que editar um item
 * não reescreve os outros — duas pessoas detalhando a mesma compra não se
 * atropelam.
 *
 * ## A consulta é uma igualdade em `transactionId`
 *
 * Sem `orderBy`: ordenar por `createdAt` junto de uma igualdade são dois campos
 * distintos, e o Firestore exigiria índice composto para uma lista que tem
 * poucos itens. A ordenação acontece em memória, como no
 * `invoice-repository.ts`, e pelo mesmo motivo — ver o cabeçalho de lá.
 *
 * ## O resumo denormalizado NÃO é escrito daqui
 *
 * `Transaction.enrichment.breakdown` é derivado dos itens, e quem o recalcula é
 * `onBreakdownItemWrite`, em `functions/`. Escrevê-lo do cliente daria duas
 * fontes de verdade para o mesmo invariante — e a segunda estaria sempre um
 * round-trip atrás da primeira, com dois aparelhos editando. A tela mostra o
 * resumo que ela mesma calcula com `breakdownRemainder` sobre os itens que tem;
 * o gravado serve às outras telas.
 */

const ORIGINS = ['manual', 'receipt', 'ai'] as const

/**
 * **A lista acima é de runtime**, e o `tsc` não a liga sozinha ao tipo. Mesma
 * armadilha de `CASH_IMPACTS`: uma origem que exista no tipo e falte aqui faz
 * **todo item daquela origem lançar na leitura** — e `'receipt'` e `'ai'` são
 * escritos por pipelines que ainda não existem, então o erro só apareceria
 * meses depois, longe de quem o causou.
 */
type Equals<A, B> =
  (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : false
const _originsCobremAUniao: Equals<BreakdownItem['origin'], (typeof ORIGINS)[number]> = true
void _originsCobremAUniao

function itemsPath(): string {
  return `households/${HOUSEHOLD_ID}/breakdownItems`
}

/**
 * Firestore → domínio.
 *
 * **`amountCents` lança em vez de cair num default.** Um item cujo valor virasse
 * 0 sairia da soma sem uma linha de erro, e o resto absorveria a diferença —
 * o invariante continuaria fechando, sobre um número errado. É a mesma linha que
 * `toTransaction` traça.
 */
export function toBreakdownItem(id: string, data: DocumentData): BreakdownItem {
  const where = `breakdownItems/${id}`
  return {
    id,
    householdId: HOUSEHOLD_ID,
    transactionId: requireText(data['transactionId'], `${where}.transactionId`),
    categoryId: requireText(data['categoryId'], `${where}.categoryId`),
    amountCents: requireCents(data['amountCents'], `${where}.amountCents`),
    description: optionalText(data['description']),
    origin: requireOneOf(data['origin'], ORIGINS, `${where}.origin`),
    receiptItemIds: textList(data['receiptItemIds']),
    createdAt: requireIso(data['createdAt'], `${where}.createdAt`),
    updatedAt: requireIso(data['updatedAt'], `${where}.updatedAt`),
  }
}

function byTransactionQuery(db: Firestore, transactionId: string): Query {
  return query(collection(db, itemsPath()), where('transactionId', '==', transactionId))
}

/** O registro que `firestore-indexes.test.ts` varre. Ver `INVOICE_QUERIES`. */
export const BREAKDOWN_QUERIES: Record<string, (db: Firestore, id: string) => Query> = {
  listByTransaction: byTransactionQuery,
}

export function createFirebaseBreakdownRepository(db: Firestore): BreakdownRepository {
  return {
    listByTransaction: async (transactionId) => {
      const snap = await getDocs(byTransactionQuery(db, transactionId))
      return snap.docs
        .map((item) => toBreakdownItem(item.id, item.data()))
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    },

    create: async (input) => {
      const created = await addDoc(collection(db, itemsPath()), {
        transactionId: input.transactionId,
        categoryId: input.categoryId,
        amountCents: input.amountCents,
        description: input.description,
        // Item criado por esta porta é alguém digitando. Ver `NewBreakdownItem`.
        origin: 'manual',
        receiptItemIds: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return created.id
    },

    update: async (id, patch) => {
      // Campo ausente é campo não tocado, e `undefined` não vai para o SDK —
      // ele o recusa. Mesma montagem de `enrichmentUpdate`.
      const update: DocumentData = { updatedAt: serverTimestamp() }
      if (patch.categoryId !== undefined) update['categoryId'] = patch.categoryId
      if (patch.amountCents !== undefined) update['amountCents'] = patch.amountCents
      if (patch.description !== undefined) update['description'] = patch.description
      await updateDoc(doc(db, itemsPath(), id), update)
    },

    remove: async (id) => {
      await deleteDoc(doc(db, itemsPath(), id))
    },
  }
}
